import { type MouseEvent, forwardRef, useEffect, useMemo } from "react";
import MuiButton, {
  // eslint-disable-next-line no-restricted-imports
  type ButtonProps as MUIButtonProps,
} from "@mui/material/Button";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { Link } from "./CustomLink";
import type { UrlObject } from "url";
import { useActiveState, mergeSx } from "../../hooks";
import { Box, CircularProgress, Stack } from "@mui/material";

export type ButtonProps = Omit<MUIButtonProps, "href" | "loadingPosition"> & {
  customColor?: string;
  /** Sets the button to a toggle button. `toggleFunc` is called on click, and the `value` determines its state. */
  toggle?: {
    /** Value is used to determine its current state. */
    value: boolean;
    /** The callback function to run on click. */
    toggleFunc: (newValue?: boolean) => void;
    /** Whether to change the content on click. Defaults to `true`. */
    changeContentOnToggle?: boolean;
    /** Used to set the style of the button in its alternative state. */
    altStyle?: {
      color?: ButtonProps["color"];
      customColor?: ButtonProps["customColor"];
      variant?: ButtonProps["variant"];
      customText?: string;
      customComponent?: React.ReactElement;
    };
  };
  /** Whether the button is in loading state. Defaults to `false`. */
  loading?: boolean;
  /** Add href to transform button into a or Link tag. */
  href?: string | UrlObject;
};

export const Button = forwardRef<any, ButtonProps>(
  (
    {
      fullWidth = false,
      onClick: userOnClick,
      toggle = {
        value: false,
        toggleFunc: () => {
          return;
        },
      },
      children,
      customColor: userCustomColor,
      sx,
      variant: userVariant = "contained",
      color: userColor,
      href,
      loading = false,
      startIcon,
      disabled,
      ...rest
    },
    ref
  ) => {
    const [content, setContent] = useActiveState(children);
    const [color, setColor] = useActiveState(userColor);
    const [customColor, setCustomColor] = useActiveState(userCustomColor);
    const [variant, setVariant] = useActiveState(userVariant);
    const theme = useTheme();

    // Needed so we can manually specify button color outside of css
    const customTheme = useMemo(() => {
      let customColorObj: any;
      if (customColor) {
        const customColorMain = {
          main: customColor,
        };

        customColorObj = theme.palette.augmentColor({
          color: customColorMain,
        });
      }
      return customColorObj
        ? createTheme(theme, {
            palette: {
              custom: customColorObj,
            },
          })
        : theme;
    }, [customColor, theme]);

    useEffect(() => {
      if (toggle) {
        const altContent =
          toggle.changeContentOnToggle ||
          toggle.changeContentOnToggle === undefined
            ? toggle.altStyle?.customComponent ||
              toggle.altStyle?.customText ||
              "Cancel"
            : children;

        setContent(!toggle.value ? children : altContent);
        setColor(toggle.value ? toggle.altStyle?.color || "error" : userColor);
        setCustomColor(
          toggle.value
            ? toggle.altStyle?.customColor || userCustomColor
            : userCustomColor
        );
        setVariant(
          toggle.value ? toggle.altStyle?.variant || userVariant : userVariant
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggle]);

    const onClick = (e: MouseEvent) => {
      // Toggle section
      if (toggle) {
        toggle.toggleFunc(!toggle.value);
      }
      // Run custom onClick last
      if (userOnClick) {
        userOnClick(e as any);
      }
    };

    return (
      <ThemeProvider theme={customTheme}>
        <MuiButton
          sx={mergeSx(
            {
              display: fullWidth ? "block" : "inherit",
              width: fullWidth ? "100%" : "inherit",
              minWidth: rest.size !== "small" ? "80px" : 0,
              position: "relative",
            },
            sx
          )}
          ref={ref}
          variant={variant}
          disabled={disabled || loading}
          color={customColor ? ("custom" as any) : color}
          onClick={onClick}
          component={
            (typeof href === "string" && href.startsWith("/")) ||
            typeof href === "object"
              ? Link
              : MuiButton
          }
          to={href}
          {...rest}
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
          >
            {loading ? (
              <CircularProgress
                color="inherit"
                size={rest.size === "small" ? 20 : 26}
              />
            ) : (
              <>
                {startIcon}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  {content}
                </Box>
              </>
            )}
          </Stack>
        </MuiButton>
      </ThemeProvider>
    );
  }
);
